import { createStore } from "zustand/vanilla"
import {
  History, createHistory, pushHistory, redoHistory, undoHistory,
} from "../lib/history"

export type DocumentState<T> = {
  history: History<T>
  /** Records a change as one undo step. A change identical to the document is ignored. */
  change: (next: T) => void
  undo: () => void
  redo: () => void
}

type Options<T> = {
  historyLimit: number
  /** Called after every change, undo and redo; never for the initial document. */
  onChange: (value: T) => void
}

export const createDocumentStore = <T>(initial: T, { historyLimit, onChange }: Options<T>) => (
  createStore<DocumentState<T>>()((set, get) => {
    const update = (history: History<T>) => {
      if (history === get().history) return

      set({ history })
      onChange(history.present)
    }

    return {
      history: createHistory(initial),
      change: (next) => update(pushHistory(get().history, next, { limit: historyLimit })),
      undo: () => update(undoHistory(get().history)),
      redo: () => update(redoHistory(get().history)),
    }
  })
)

export type DocumentStore<T> = ReturnType<typeof createDocumentStore<T>>
